import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Eye, Users, DollarSign, TrendingUp, Star, Calendar,
  BarChart3, PieChart, ArrowUp, ArrowDown, Loader2,
  ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { analyticsApi } from '@/api';

// Simple bar chart component
const SimpleBarChart = ({ data, dataKey, labelKey, color = '#f97316', maxBars = 7 }) => {
  if (!data || data.length === 0) return <div className="text-gray-400 text-center py-8">Aucune donnée</div>;
  
  const displayData = data.slice(-maxBars);
  const maxValue = Math.max(...displayData.map(d => d[dataKey])) || 1;
  
  return (
    <div className="flex items-end gap-1 h-32">
      {displayData.map((item, index) => {
        const height = (item[dataKey] / maxValue) * 100;
        return (
          <div key={index} className="flex-1 flex flex-col items-center gap-1">
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: `${height}%` }}
              transition={{ delay: index * 0.05, duration: 0.3 }}
              className="w-full rounded-t"
              style={{ backgroundColor: color, minHeight: item[dataKey] > 0 ? '4px' : '0' }}
              title={`${item[labelKey]}: ${item[dataKey]}`}
            />
            <span className="text-xs text-gray-400 truncate w-full text-center">
              {item[labelKey]?.slice(-5) || ''}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// Stat card component
const StatCard = ({ icon: Icon, label, value, subValue, trend, color = 'text-primary' }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-xl p-4 shadow-sm border"
  >
    <div className="flex items-start justify-between">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color.replace('text-', 'bg-')}/10`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 text-xs ${trend >= 0 ? 'text-green-600' : 'text-red-500'}`}>
          {trend >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
          {Math.abs(trend)}%
        </div>
      )}
    </div>
    <div className="mt-3">
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
      {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
    </div>
  </motion.div>
);

// Experience performance row
const ExperienceRow = ({ title, views, bookings, revenue, rank }) => (
  <div className="flex items-center gap-3 py-3 border-b last:border-b-0">
    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
      {rank}
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-medium truncate">{title}</p>
      <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
        <span className="flex items-center gap-1">
          <Eye className="w-3 h-3" /> {views}
        </span>
        <span className="flex items-center gap-1">
          <Users className="w-3 h-3" /> {bookings}
        </span>
        {revenue > 0 && (
          <span className="flex items-center gap-1 text-green-600">
            <DollarSign className="w-3 h-3" /> {revenue}€
          </span>
        )}
      </div>
    </div>
  </div>
);

// Rating distribution bar
const RatingBar = ({ rating, count, total }) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm w-3">{rating}</span>
      <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          className="h-full bg-yellow-400 rounded-full"
        />
      </div>
      <span className="text-xs text-gray-500 w-8">{count}</span>
    </div>
  );
};

const GuideAnalyticsDashboard = () => {
  const { i18n } = useTranslation();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const data = await analyticsApi.getExplorerAnalytics(period);
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const periodLabels = {
    '7d': i18n.language === 'fr' ? '7 derniers jours' : 'Last 7 days',
    '30d': i18n.language === 'fr' ? '30 derniers jours' : 'Last 30 days',
    '90d': i18n.language === 'fr' ? '3 derniers mois' : 'Last 3 months',
    '365d': i18n.language === 'fr' ? '12 derniers mois' : 'Last 12 months',
    'all': i18n.language === 'fr' ? 'Tout' : 'All time'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12 text-gray-500">
        {i18n.language === 'fr' ? 'Impossible de charger les analytics' : 'Failed to load analytics'}
      </div>
    );
  }

  const { overview, views, bookings, revenue, reviews } = analytics;

  // Merge data for experience performance
  const experiencePerformance = views.by_experience.map(v => {
    const bookingData = bookings.by_experience.find(b => b.experience_id === v.experience_id);
    const revenueData = revenue.by_experience.find(r => r.experience_id === v.experience_id);
    return {
      ...v,
      bookings: bookingData?.bookings || 0,
      revenue: revenueData?.revenue || 0
    };
  });

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header with period selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-primary" />
          {i18n.language === 'fr' ? 'Analytics' : 'Analytics'}
        </h2>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[180px]" data-testid="period-selector">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(periodLabels).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={Eye}
          label={i18n.language === 'fr' ? 'Vues totales' : 'Total Views'}
          value={overview.total_views}
          color="text-blue-500"
        />
        <StatCard
          icon={Users}
          label={i18n.language === 'fr' ? 'Réservations' : 'Bookings'}
          value={overview.total_bookings}
          subValue={`${overview.completed_bookings} ${i18n.language === 'fr' ? 'terminées' : 'completed'}`}
          color="text-green-500"
        />
        <StatCard
          icon={DollarSign}
          label={i18n.language === 'fr' ? 'Revenus' : 'Revenue'}
          value={`${overview.total_revenue}€`}
          subValue={`${overview.pending_payout}€ ${i18n.language === 'fr' ? 'en attente' : 'pending'}`}
          color="text-primary"
        />
        <StatCard
          icon={TrendingUp}
          label={i18n.language === 'fr' ? 'Taux de conversion' : 'Conversion Rate'}
          value={`${overview.conversion_rate}%`}
          subValue={`${overview.total_views} ${i18n.language === 'fr' ? 'vues' : 'views'} → ${overview.confirmed_bookings} ${i18n.language === 'fr' ? 'réservations' : 'bookings'}`}
          color="text-purple-500"
        />
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Views Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-5 shadow-sm border"
        >
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Eye className="w-4 h-4 text-blue-500" />
            {i18n.language === 'fr' ? 'Vues par jour' : 'Views per Day'}
          </h3>
          <SimpleBarChart
            data={views.by_day}
            dataKey="count"
            labelKey="date"
            color="#3b82f6"
          />
        </motion.div>

        {/* Revenue Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-5 shadow-sm border"
        >
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-primary" />
            {i18n.language === 'fr' ? 'Revenus par jour' : 'Revenue per Day'}
          </h3>
          <SimpleBarChart
            data={revenue.by_day}
            dataKey="amount"
            labelKey="date"
            color="#f97316"
          />
        </motion.div>
      </div>

      {/* Experience Performance & Reviews */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Experiences */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-5 shadow-sm border"
        >
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <PieChart className="w-4 h-4 text-green-500" />
            {i18n.language === 'fr' ? 'Performance par expérience' : 'Experience Performance'}
          </h3>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {experiencePerformance.length > 0 ? (
              experiencePerformance.slice(0, 5).map((exp, idx) => (
                <ExperienceRow
                  key={exp.experience_id}
                  title={exp.title}
                  views={exp.views}
                  bookings={exp.bookings}
                  revenue={exp.revenue}
                  rank={idx + 1}
                />
              ))
            ) : (
              <p className="text-gray-400 text-center py-4">
                {i18n.language === 'fr' ? 'Aucune donnée disponible' : 'No data available'}
              </p>
            )}
          </div>
        </motion.div>

        {/* Reviews */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl p-5 shadow-sm border"
        >
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Star className="w-4 h-4 text-yellow-500" />
            {i18n.language === 'fr' ? 'Avis' : 'Reviews'}
          </h3>
          <div className="flex items-center gap-4 mb-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-primary">{overview.avg_rating || '-'}</p>
              <div className="flex items-center gap-0.5 justify-center">
                {[1, 2, 3, 4, 5].map(star => (
                  <Star
                    key={star}
                    className={`w-3 h-3 ${star <= overview.avg_rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`}
                  />
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {overview.total_reviews} {i18n.language === 'fr' ? 'avis' : 'reviews'}
              </p>
            </div>
            <div className="flex-1 space-y-1">
              {[5, 4, 3, 2, 1].map(rating => (
                <RatingBar
                  key={rating}
                  rating={rating}
                  count={reviews.distribution[rating] || 0}
                  total={overview.total_reviews}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Key Metrics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-gradient-to-r from-primary/5 to-orange-50 rounded-xl p-5 border border-primary/20"
      >
        <h3 className="font-semibold mb-3">
          {i18n.language === 'fr' ? 'Résumé de la période' : 'Period Summary'}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-500">{i18n.language === 'fr' ? 'Expériences actives' : 'Active Experiences'}</p>
            <p className="font-bold text-lg">{analytics.experiences_count}</p>
          </div>
          <div>
            <p className="text-gray-500">{i18n.language === 'fr' ? 'Taux d\'achèvement' : 'Completion Rate'}</p>
            <p className="font-bold text-lg">{overview.completion_rate}%</p>
          </div>
          <div>
            <p className="text-gray-500">{i18n.language === 'fr' ? 'Annulations' : 'Cancellations'}</p>
            <p className="font-bold text-lg">{overview.cancelled_bookings}</p>
          </div>
          <div>
            <p className="text-gray-500">{i18n.language === 'fr' ? 'Revenus libérés' : 'Released Earnings'}</p>
            <p className="font-bold text-lg text-green-600">{overview.released_payout}€</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default GuideAnalyticsDashboard;
